import { useState, useRef, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
    TextField,
    PrimaryButton,
    DefaultButton,
    IconButton,
    ProgressIndicator,
} from "@fluentui/react";
import { toast } from 'react-toastify';
import uploadIcon from "images/upload.svg";
import { environment } from "environments/environment";
import { createComment, updateComment } from "store/actions/commentActions";
import { fetchPresignedUrl, uploadToS3, deleteFromS3 } from "store/actions/storageActions";
import { generateGuid } from "utils/guid";
import "./AddComment.css";

const AddComment = ({
    workItemId,
    editingComment = null,
    onSuccess,
    onCancel,
    initialData = { comment: '' },
    fileConfig = {
        accept: 'image/png,image/jpg,image/jpeg,application/pdf',
        validMimeTypes: ['image/png', 'image/jpg', 'image/jpeg', 'application/pdf'],
        maxFileSize: 20971520, // 20MB
        allowMultiple: true,
    },
    submitButtonText = "Comment",
    placeholder = "Enter your comment...",
    maxLength = 1000,
}) => {
    const dispatch = useDispatch();
    const fileInputRef = useRef(null);

    const { formLoading: commentLoading } = useSelector((state) => state.comment);

    const [formData, setFormData] = useState(initialData);
    const [files, setFiles] = useState([]);
    const [uploadingFiles, setUploadingFiles] = useState([]);
    const [errors, setErrors] = useState({});

    // Populate form when editing
    useEffect(() => {
        if (editingComment) {
            setFormData({ comment: editingComment.comment || '' });
            if (editingComment.attachments && editingComment.attachments.length > 0) {

                // Populate files array for form submission
                const processedFiles = editingComment.attachments.map(att => ({
                    id: att.id,
                    name: att.name,
                    key: att.key,
                    type: att.contentType || att.type || att.mimeType || 'application/octet-stream', // Include contentType from API
                    url: att.url || `https://${environment.storage.s3.mediaBucketName}.s3.amazonaws.com/${att.key}`,
                }));

                setFiles(processedFiles);

                // Populate uploadingFiles array for UI display with completed status
                setUploadingFiles(editingComment.attachments.map(att => ({
                    name: att.name,
                    progress: 100,
                    isUploading: false,
                    error: null,
                })));

            }
        } else {
            setFormData(initialData);
            setFiles([]);
            setUploadingFiles([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editingComment]);

    const validateFileType = (file) => {
        return fileConfig.validMimeTypes.includes(file.type);
    };

    const validateFileSize = (file) => {
        return file.size <= fileConfig.maxFileSize;
    };

    const handleFileClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = async (event) => {
        const selectedFiles = Array.from(event.target.files);

        if (selectedFiles.length === 0) return;

        const validFiles = [];

        for (const file of selectedFiles) {
            // Validate file type
            if (!validateFileType(file)) {
                toast.error('Invalid format. Please upload JPG/JPEG/PNG type images or PDF.');
                continue;
            }

            // Validate file size
            if (!validateFileSize(file)) {
                toast.error(`Document size cannot be greater than ${fileConfig.maxFileSize / 1024 / 1024}MB.`);
                continue;
            }

            validFiles.push(file);
        }

        if (validFiles.length > 0) {
            await uploadFiles(validFiles);
        }

        // Reset input
        event.target.value = '';
    };

    const uploadFiles = async (filesToUpload) => {
        const newUploadingFiles = filesToUpload.map(file => ({
            name: file.name,
            progress: 0,
            isUploading: true,
            error: null,
        }));

        setUploadingFiles(prev => [...prev, ...newUploadingFiles]);

        for (let i = 0; i < filesToUpload.length; i++) {
            const file = filesToUpload[i];
            const fileIndex = uploadingFiles.length + i;

            try {
                // Generate unique key with GUID
                const guid = generateGuid();
                const fileExtension = file.name.substring(file.name.lastIndexOf('.'));
                const key = `${environment.storage.prefix.attachment}${guid}${fileExtension}`;

               
                const params = {
                    bucketName: environment.storage.s3.mediaBucketName,
                    key: key,
                    httpVerb: 2, 
                    contentType: file.type
                };

                const presignedData = await dispatch(fetchPresignedUrl(params));
                const presignedUrl = presignedData.url || presignedData;

                await dispatch(uploadToS3(
                    presignedUrl,
                    file.type,
                    file,
                    (progress) => {
                        setUploadingFiles(prev => {
                            const updated = [...prev];
                            if (updated[fileIndex]) {
                                updated[fileIndex].progress = progress;
                            }
                            return updated;
                        });
                    }
                ));

                setUploadingFiles(prev => {
                    const updated = [...prev];
                    if (updated[fileIndex]) {
                        updated[fileIndex].isUploading = false;
                        updated[fileIndex].progress = 100;
                    }
                    return updated;
                });

                setFiles(prev => [...prev, {
                    name: file.name,
                    key: key,
                    type: file.type,
                    url: `https://${environment.storage.s3.mediaBucketName}.s3.amazonaws.com/${key}`,
                }]);

            } catch (error) {
                setUploadingFiles(prev => {
                    const updated = [...prev];
                    if (updated[fileIndex]) {
                        updated[fileIndex].isUploading = false;
                        updated[fileIndex].error = 'Upload failed';
                    }
                    return updated;
                });
                toast.error(`Failed to upload ${file.name}`);
            }
        }
    };

    const handleRemoveUploadingFile = async (index) => {
        const fileToRemove = uploadingFiles[index];

        // If file has finished uploading and is in files array, delete from S3
        if (!fileToRemove.isUploading && !fileToRemove.error) {

            const fileInFilesArray = files.find(f => f.name === fileToRemove.name);
            if (fileInFilesArray && fileInFilesArray.key) {
                try {
                    const params = {
                        bucketName: environment.storage.s3.mediaBucketName,
                        key: fileInFilesArray.key,
                        httpVerb: 3, // DELETE operation
                        contentType: fileInFilesArray.type
                    };

                    const presignedUrlResponse = await dispatch(fetchPresignedUrl(params));

                    // Extract URL from response (handle different response formats)
                    const presignedUrl = presignedUrlResponse?.url ||
                                        presignedUrlResponse?.data?.url ||
                                        presignedUrlResponse?.data ||
                                        presignedUrlResponse;

                    if (!presignedUrl || typeof presignedUrl !== 'string') {
                        throw new Error('Invalid presigned URL received from backend');
                    }

                    await dispatch(deleteFromS3(presignedUrl));

                
                    setFiles(prev => prev.filter(f => f.name !== fileToRemove.name));
                } catch (error) {
                    console.error('Failed to delete file from S3:', error);
                    toast.error(`Failed to delete ${fileToRemove.name} from storage`);
                    return; 
                }
            }
        }

        // Remove from uploading files list
        setUploadingFiles(prev => prev.filter((_, i) => i !== index));
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.comment || !formData.comment.trim()) {
            newErrors.comment = "Comment is required.";
        } 

        if (uploadingFiles.some(f => f.isUploading)) {
            newErrors.comment = "Please wait for all files to finish uploading.";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateField = (name, value) => {
        const newErrors = { ...errors };

        if (name === "comment") {
            if (!value || !value.trim()) {
                newErrors.comment = "Comment is required.";
            } else {
                newErrors.comment = "";
            }
        }

        setErrors(newErrors);
    };

    const handleBlur = () => {
        validateField("comment", formData.comment);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        const data = {
            id: editingComment ? editingComment.id : 0,
            workItemId: workItemId,
            comment: formData.comment.trim(),
            attachments: files.map(f => ({
                id: f.id || 0,
                name: f.name,
                key: f.key,
            }))
        };

        try {
            if (editingComment) {
                await dispatch(updateComment('/comments', data));
            } else {
                await dispatch(createComment('/comments', data));
            }

            // Reset form
            setFormData({ comment: '' });
            setFiles([]);
            setUploadingFiles([]);
            setErrors({});

            // Call success callback
            if (onSuccess) {
                onSuccess();
            }
        } catch (error) { }
    };

    const handleCancel = () => {
        setFormData({ comment: '' });
        setFiles([]);
        setUploadingFiles([]);
        setErrors({});

        if (onCancel) {
            onCancel();
        }
    };

    return (
        <form className="form-content" onSubmit={handleSubmit}>
            <div>
                <div className="teams-card">
                    <div className="row">
                        <div className="col-12">
                            <TextField
                                className="textarea"
                                label={
                                    <span>
                                        Comment <span style={{ color: "red" }}>*</span>{" "}
                                        <span style={{ fontSize: "12px", color: "#666" }}>
                                            ({formData.comment.length}/{maxLength})
                                        </span>
                                    </span>
                                }
                                placeholder={placeholder}
                                multiline
                                rows={4}
                                value={formData.comment}
                                onChange={(e, newValue) => {
                                    setFormData({ ...formData, comment: newValue || '' });
                                    validateField("comment", newValue || '');
                                }}
                                onBlur={handleBlur}
                                maxLength={maxLength}
                                errorMessage={errors.comment}
                            />
                        </div>
                    </div>

                    <input
                        ref={fileInputRef}
                        type="file"
                        accept={fileConfig.accept}
                        multiple={fileConfig.allowMultiple}
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                    />

                    <div
                        className="upload-container"
                        onClick={handleFileClick}
                        style={{ cursor: 'pointer', marginTop: '15px' }}
                    >
                        <img src={uploadIcon} alt="upload-image" />
                        <div>
                            <div className="upload-text">Upload Attachment</div>
                            <div className="text-muted">
                                {fileConfig.accept.split(',').map(t => t.split('/')[1]).join(', ')}
                                (max {fileConfig.maxFileSize / 1024 / 1024}MB)
                            </div>
                        </div>
                    </div>

                    {/* Uploading files */}
                    {uploadingFiles.length > 0 && (
                        <div style={{ marginTop: '15px' }}>
                            {uploadingFiles.map((file, index) => (
                                <div key={index} style={{
                                    padding: '12px',
                                    backgroundColor: '#f3f2f1',
                                    borderRadius: '4px',
                                    marginBottom: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between'
                                }}>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontSize: '14px', color: '#323130', marginBottom: '4px' }}>
                                            {file.name}
                                        </div>
                                        {file.isUploading && (
                                            <ProgressIndicator
                                                percentComplete={file.progress / 100}
                                                description="Uploading..."
                                            />
                                        )}
                                        {file.error && (
                                            <div style={{ fontSize: '12px', color: '#a4262c' }}>
                                                {file.error}
                                            </div>
                                        )}
                                        {!file.isUploading && !file.error && (
                                            <div style={{ fontSize: '12px', color: '#107c10' }}>
                                                ✓ Uploaded
                                            </div>
                                        )}
                                    </div>
                                    {!file.isUploading && (
                                        <IconButton
                                            iconProps={{ iconName: 'Delete' }}
                                            title="Remove"
                                            ariaLabel="Remove file"
                                            onClick={() => handleRemoveUploadingFile(index)}
                                        />
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="col-12">
                        <div className="d-flex justify-content-end">
                            <DefaultButton
                                text="Cancel"
                                onClick={handleCancel}
                                disabled={commentLoading || uploadingFiles.some(f => f.isUploading)}
                                style={{ marginTop: "15px" }}
                            />
                            <PrimaryButton
                                text={submitButtonText}
                                type="submit"
                                style={{ marginTop: "15px" }}
                                disabled={commentLoading || uploadingFiles.some(f => f.isUploading)}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </form>
    );
};

export default AddComment;
